import { Ref, ComputedRef, computed } from '@nuxtjs/composition-api';
import { PromotionOption, TierGift } from './cart/utils';

export interface WheelGift {
  name: string;
  thumbnail: string;
  stock: number;
  sku: string;
  minimumTier: number;
  minimumSubtotal: number;
}

export function useNextLevelAmount(
  cartPromotions: Ref<{
    options: (Partial<PromotionOption> | null | undefined)[] | null | undefined;
    eligiblePromotionIdx: number;
    isSpinTheWheel: boolean;
  }>,
  tierIdx?: ComputedRef<number | null>
) {
  const nextLevelAmount = computed(() => {
    // If a tier is specified, use that tier
    // If not, check the tier the user is eligible for
    const eligibleTierIdx = tierIdx?.value ?? cartPromotions.value.eligiblePromotionIdx;

    // If there are no tiers, return 0
    if (!cartPromotions.value.options?.length) {
      return 0;
    }

    // If there are no eligible tiers or the first tier is eligible, return the first tier minimum subtotal
    if (eligibleTierIdx === 0) {
      return cartPromotions.value.options?.[0]?.is_eligible
        ? cartPromotions.value.options?.[1]?.tier_minimum_subtotal || 0
        : cartPromotions.value.options?.[0]?.tier_minimum_subtotal || 0;
    }

    // If the last tier is eligible, return the last tier minimum subtotal
    if (eligibleTierIdx === cartPromotions.value.options.length - 1) {
      return cartPromotions.value.options?.[eligibleTierIdx]?.tier_minimum_subtotal || 0;
    }

    // If the eligible tier is in the middle, return the next tier minimum subtotal
    return cartPromotions.value.options?.[eligibleTierIdx + 1]?.tier_minimum_subtotal || 0;
  });

  return {
    nextLevelAmount,
  };
}

export function useGiftTiers(
  nextLevelAmount: Ref<number>,
  cartAmount: Ref<number>,
  cartPriceRuleDiscounts: ComputedRef<{
    tiers: {
      discountValue: number;
    }[];
    eligibleTierIdx: number;
  }>
) {
  const amountRemaining = computed(() => {
    if (!nextLevelAmount.value) {
      return 0;
    }

    return Math.max(Math.round(nextLevelAmount.value - cartAmount.value), 0);
  });

  const progressDone = computed(() => {
    if (!nextLevelAmount.value) {
      return 0;
    }

    return Math.min((cartAmount.value / nextLevelAmount.value) * 100, 100);
  });

  const currentTier = computed(() => {
    return progressDone ? cartPriceRuleDiscounts.value.eligibleTierIdx + 1 : 0;
  });

  const nextTierDiscount = computed(() => {
    return cartPriceRuleDiscounts.value.tiers?.[currentTier.value]?.discountValue;
  });

  return {
    amountRemaining,
    progressDone,
    currentTier,
    nextTierDiscount,
  };
}

/**
 * Returns compiled gift options with each showing the minimum tier and subtotal required
 * Note: this is only used for the spinning wheel
 */
export function useWheelGifts(cartPromotions: ComputedRef<PromotionOption[]>) {
  // Compile all gifts into one array with no duplicates
  // Each gift will have a minimum tier and subtotal required
  const wheelGifts = computed(() => {
    if (!cartPromotions.value?.length) {
      return [];
    }
    const gifts = cartPromotions.value.reduce((acc, currTier, currIdx) => {
      (currTier.gift as TierGift[]).forEach((currGift: TierGift) => {
        // Validate that the gift is not already in the array
        const isGiftInArray = acc.some((gift: WheelGift) => gift.name === currGift.name);

        if (!isGiftInArray) {
          const newWheelGift: WheelGift = {
            name: currGift.name,
            thumbnail: currGift.thumbnail,
            stock: currGift.stock,
            minimumTier: currIdx + 1,
            minimumSubtotal: currTier.tier_minimum_subtotal as number,
            sku: currGift.sku,
          };

          acc.push(newWheelGift);
        }
      });
      return acc;
    }, [] as WheelGift[]);

    return gifts;
  });

  return {
    wheelGifts,
  };
}
